import { getVariantToken } from '@koaly/mixins';
import { withExtra } from '@koaly/with-extra';
import FocusTrap from 'focus-trap-react';
import React, { useMemo } from 'react';
import ScrollLock, { TouchScrollable } from 'react-scrolllock';
import { CSSTransition } from 'react-transition-group';
import { useKey } from 'react-use';
import styled from 'styled-components';
import { CssTransition } from '../../animations/CssTransition.js';
import { FadeAnimation } from '../../animations/FadeAnimation.js';
import { Dimmer } from '../Dimmer/Dimmer.js';
import { Portal } from '../Portal/Portal.js';
import { DrawerSlideLeftAnimation, DrawerSlideRightAnimation, DrawerSlideTopAnimation, DrawerSlideBottomAnimation } from './animation/DrawerSlide.js';
import { DrawerProvider } from './DrawerProvider.js';

const StyledDrawer = styled.div `
  top: 0;
  max-width: ${({ width }) => (width === 'auto' || width === '100%' ? '100%' : width + 'px')};
  width: ${({ width }) => (width !== 'auto' ? '100%' : width)};
  z-index: ${({ zIndex }) => zIndex};
  height: 100%;
  outline: none;
  position: fixed;
  overflow: scroll;
  overflow-y: scroll;
  -webkit-overflow-scrolling:touch;

  background-color: ${getVariantToken('drawer', 'backgroundColor')};

  ${({ position }) => position !== 'right' && 'left: 0;'}
  ${({ position }) => position === 'right' && 'right: 0;'}
  &.enter {
    ${({ animation }) => animation.getCss('enter')};
  }

  &.enter-active {
    ${({ animation }) => animation.getCss('enterActive')};
  }

  &.exit {
    ${({ animation }) => animation.getCss('exit')};
  }

  &.exit-active {
    ${({ animation }) => animation.getCss('exitActive')};
  }
`;
const StyledAnimatedDimmer = styled(Dimmer) `
  &.appear {
    ${({ animation }) => animation.getCss('enter')};
  }

  &.appear-active {
    ${({ animation }) => animation.getCss('enterActive')};
  }

  &.exit {
    ${({ animation }) => animation.getCss('exit')};
  }

  &.exit-active {
    ${({ animation }) => animation.getCss('exitActive')};
  }
`;
const DrawerUtilities = ({ onClose, hasScrollLock }) => {
    useKey('Escape', () => {
        if (onClose) {
            onClose();
        }
    });
    return React.createElement(ScrollLock, { isActive: hasScrollLock });
};
const Drawer = withExtra(({ isShown, children, onClose, zIndex, position, width, variant, hasScrollLock, className }) => {
    const animationDuration = 300;
    const animationLeft = useMemo(() => new CssTransition(DrawerSlideLeftAnimation(animationDuration)), []);
    const animationRight = useMemo(() => new CssTransition(DrawerSlideRightAnimation(animationDuration)), []);
    const animationTop = useMemo(() => new CssTransition(DrawerSlideTopAnimation(animationDuration)), []);
    const animationBottom = useMemo(() => new CssTransition(DrawerSlideBottomAnimation(animationDuration)), []);
    const animation = useMemo(() => {
        switch (position) {
            case 'right':
                return animationRight;
            case 'top':
                return animationTop;
            case 'bottom':
                return animationBottom;
            default:
                return animationLeft;
        }
    }, [animationBottom, animationLeft, animationRight, animationTop, position]);
    const fadeAnimation = new CssTransition(FadeAnimation(animationDuration));
    return (React.createElement(DrawerProvider, { close: onClose },
        React.createElement(CSSTransition, { in: isShown, timeout: animationDuration, unmountOnExit: true },
            React.createElement(Portal, null,
                React.createElement(FocusTrap, { focusTrapOptions: { clickOutsideDeactivates: true, fallbackFocus: 'div' } },
                    React.createElement(TouchScrollable, null,
                        React.createElement(StyledDrawer, { className: className, "aria-modal": "true", role: "dialog", animation: animation, width: width, zIndex: zIndex, position: position, variant: variant }, children))),
                React.createElement(DrawerUtilities, { onClose: onClose, hasScrollLock: hasScrollLock }),
                React.createElement(CSSTransition, { timeout: animationDuration, appear: true, in: isShown, unmountOnExit: true },
                    React.createElement(StyledAnimatedDimmer, { animation: fadeAnimation, onClick: onClose, zIndex: zIndex - 10 }))))));
}, {
    name: 'Drawer',
});
Drawer.defaultProps =
    {
        position: 'left',
        zIndex: 110,
        variant: 'primary',
        hasScrollLock: true,
    };

export { Drawer, DrawerUtilities };
//# sourceMappingURL=Drawer.js.map
